import { addYears, differenceInMonths, differenceInYears } from 'date-fns';
import { differenceInDays } from 'date-fns/differenceInDays';
import { formatWithOptions } from 'date-fns/fp';
import { nl } from 'date-fns/locale';
import { flow } from 'es-toolkit';
import { match, P } from 'ts-pattern';
import { DateValue } from 'react-aria-components';
import { CalendarDate } from '@internationalized/date';

// example: 22/12/2024
const dateToString = formatWithOptions({ locale: nl }, 'dd-MM-yyyy');
const dateToDateInputValue = formatWithOptions({ locale: nl }, 'yyyy-MM-dd');

export const formatDate = (date: Date) => dateToString(date);
export const formatToDateInput = (date: Date) => dateToDateInputValue(date);

export function formatDateDifference(fromDate: Date, toDate = new Date()) {
  const years = differenceInYears(toDate, fromDate);
  const months = differenceInMonths(toDate, addYears(fromDate, years));

  return match({ years, months })
    .returnType<string>()
    .with(
      { years: P.when((y) => !!y), months: P.when((m) => !!m) },
      () => `${years} jaar & ${months} maanden`,
    )
    .with(
      { years: P.when((y) => !y), months: P.when((m) => !!m) },
      () => `${months} maanden`,
    )
    .with(
      { years: P.when((y) => !!y), months: P.when((m) => !m) },
      () => `${years} jaar`,
    )
    .otherwise(() => formatDate(fromDate));
}

export function mapDaysToLabel(days: number) {
  const isPast = (days: number) => days < 0;

  return match(days)
    .returnType<'verlopen' | 'vandaag' | `${number} dag` | `${number} dagen`>()
    .with(P.when(isPast), () => `verlopen`)
    .with(0, () => `vandaag`)
    .with(1, (v) => `${v} dag`)
    .with(P._, (v) => `${v} dagen`)
    .exhaustive();
}

export const getDifferenceInDaysLabel = flow(differenceInDays, mapDaysToLabel);
export const getDifferenceInDaysLabelFromNow = (toDate: Date) =>
  getDifferenceInDaysLabel(new Date(), toDate);

export function formatDateInputValue(params: DateValue | null) {
  if (!params) {
    return null;
  }
  return new Date(params.year, params.month - 1, params.day);
}

export function formatDateToCalendarDate(date: Date | null) {
  if (!date) {
    return null;
  }
  const calendarDate = new CalendarDate(
    date.getFullYear(),
    date.getMonth() + 1,
    date.getDate(),
  );

  return calendarDate;
}

export function getTodayInCalendarDate() {
  const now = new Date();
  const calendarDate = formatDateToCalendarDate(now);
  return calendarDate;
}
